/**
 * The grid-agnostic area-template geometry an AoE attack fans out over, and the shared
 * damage pool it draws once upfront.
 */

import type { Cell, EncounterState, WeaponDamage, WeaponRange } from "@lancer-vtt/domain";
import type { AttackTarget, RequestAttack } from "@lancer-vtt/shared";
import { rollDamage, rollOverkill, type DiceSource } from "../../dice";
import type { AreaSelector } from "../effects";
import { keepHighest } from "./damage";

// ── area-weapons Phase 1/2: the shared-pool area path on the grid-agnostic seam ─────

/** The four Lancer area-template kinds a weapon's frozen range can carry. */
type AreaKind = "blast" | "burst" | "line" | "cone";

/**
 * The weapon's **area template** — its kind + size (radius/length in spaces) — read off the
 * frozen range, or `undefined` for a single-target weapon. Priority `blast > burst > line > cone`
 * disambiguates the (unusual) multi-area range; the size is the largest value of the chosen kind.
 * This is the one place {@link resolveAreaAttack} decides which grid-agnostic template to fan out.
 */
export function areaShapeOf(range: WeaponRange[]): { kind: AreaKind; size: number } | undefined {
  for (const kind of ["blast", "burst", "line", "cone"] as AreaKind[]) {
    let size = 0;
    let found = false;
    for (const r of range) {
      if (r.kind === kind && r.val > size) {
        size = r.val;
        found = true;
      } else if (r.kind === kind) {
        found = true;
      }
    }
    if (found) return { kind, size };
  }
  return undefined;
}

/**
 * The tagged origin {@link Cell} a Blast radiates from — the aimed cell directly (a `{kind:"cell"}`
 * target), or the anchor `pos` of a targeted unit. Grid-agnostic (works on hex and square alike,
 * unlike {@link aimCell}'s square-only read). `undefined` when a unit target is unplaced.
 */
function blastOrigin(state: EncounterState, target: AttackTarget): Cell | undefined {
  if (target.kind === "cell") return target.cell as Cell;
  return state.combatants[target.id]?.pos ?? undefined;
}

/**
 * The two **shared, rolled-once** area damage values (`decisions/0051` D1) an AoE attack draws — the
 * dice for a **base** (non-crit) hit and a **crit** hit — plus the instance's flat damage addend and
 * any overkill heat (emitted once, on the wielder). Each target's own crit flag selects which value
 * it takes; the dice are drawn **once, upfront** (roll-timing — see the D1 addendum), never per target.
 */
type SharedPool = {
  /** The base pool's kept dice (`count` dice), in draw order. */
  baseDice: number[];
  /** The crit pool's kept dice — `keepHighest` of the base pool extended by one more `count`-dice set. */
  critDice: number[];
  /** The instance's flat damage addend — the `+N` of `XdY+N`, carried through **unhalved** (D2 amendment). */
  bonus: number;
  /** Overkill reroll heat (0 for a non-overkill weapon) — the wielder takes it **once**. */
  overkillHeat: number;
};

/**
 * The injected shared damage a per-target Blast resolution applies instead of rolling its own —
 * **one entry per damage instance**, index-aligned with `[weapon.damage, ...(weapon.riders ?? [])]`
 * (`npc-weapon-rider-damage` Phase 3, decision 8 — extending the Phase-1 primary-only shape to
 * riders). `resolveSingleTarget`'s per-instance loop picks entry `i`'s `baseDice`/`critDice` by
 * that target's own crit flag, adds the flat `bonus` addend, and mitigates per instance, exactly
 * like the Phase-1 primary-only path did — just looped over every instance now.
 */
export type SharedPoolInject = { baseDice: number[]; critDice: number[]; bonus: number }[];

/**
 * Roll **one damage instance's** shared pool **once, upfront** (roll-timing settled for
 * `decisions/0051` D1 — see the addendum; extended to riders by `npc-weapon-rider-damage` Phase 3
 * decision 8): the base `count` dice and one more `count`-dice crit extension, drawn before any
 * to-hit. The base value is the `count` base dice; the crit value keeps the highest `count` of the
 * combined `2 × count` (the same `keepHighest` single-target crits use). A `count: 0` flat entry —
 * every shipping rider — draws no dice. `overkill` is the WEAPON-wide tag (shared by every
 * instance, primary and rider alike); overkill rerolls settle both draws and their heat is summed
 * (emitted once by the area path, across every instance).
 *
 * **Area mode does NOT halve anything here** (`decisions/0051` D2, amended 2026-07-29). RAW's
 * "bonus damage is halved" (Core p.67's sidebar + the p.78 COMBAT TERMINOLOGY glossary — NOT p.71,
 * which is the BARRAGE aux-rider line `noBonus` cites) is the *defined term* **BONUS DAMAGE** —
 * "extra damage … that is added onto melee or ranged attacks" from a talent/system/weapon clause —
 * **not** a weapon's own listed damage. `WeaponDamage.bonus` is the flat `+N` of the printed `XdY+N`
 * expression, i.e. base damage, so halving it was a category error that silently gutted every NPC
 * area weapon (NPC profiles freeze as `{count:0,sides:0,bonus:N}` — the addend IS 100% of their
 * damage, so a Blast NPC dealt `ceil(N/2)`). RAW's halving belongs on the bonus-damage side instead:
 * attack-triggered `extra_damage` atoms (`ms_hunter_lock`'s +3, `t_crack_shot-2`'s +1d6).
 *
 * **That gap is now closed in the ENGINE** (`area-bonus-damage-halving` Phase 1) — but still not
 * here, and it never will be: the halving lives in `interpretExtraDamage`
 * (`effects/interpret.ts`), gated on the atom's own `bonusDamage: true` opt-in AND the firing
 * context's `areaAttack`. Nothing in this function's shared pool is bonus damage, so this
 * paragraph's rule stands unchanged. (Flagging the shipped content atoms is a separate slice —
 * until they carry the flag, the engine rule is reachable but no shipped rider halves.)
 */
export function rollSharedPool(entry: WeaponDamage, overkill: boolean, dice: DiceSource): SharedPool {
  const { count, sides, bonus } = entry;
  if (count === 0) {
    return { baseDice: [], critDice: [], bonus, overkillHeat: 0 };
  }
  if (overkill) {
    const base = rollOverkill(dice, count, sides);
    const ext = rollOverkill(dice, count, sides);
    const baseDice = base.dice.map((d) => d.final);
    const combined = [...baseDice, ...ext.dice.map((d) => d.final)];
    return { baseDice, critDice: keepHighest(combined, count), bonus, overkillHeat: base.heat + ext.heat };
  }
  const baseDice = rollDamage({ source: dice, count, sides }).draws.map((d) => d.value);
  const extDice = rollDamage({ source: dice, count, sides }).draws.map((d) => d.value);
  return { baseDice, critDice: keepHighest([...baseDice, ...extDice], count), bonus, overkillHeat: 0 };
}

/**
 * The per-shape geometry an {@link resolveAreaAttack} fan-out uses: the {@link AreaSelector} handed
 * to `enumerateArea`, the recorded `site`/`toward` inputs it needs, and the Blast-only LoS/cover
 * `measureFrom` anchor (`decisions/0051` D4). `undefined` means "can't build the template" (an
 * unplaced attacker / unit-target with no position) — the caller degrades to single-target.
 */
type AreaTemplate = {
  selector: AreaSelector;
  site?: Cell;
  toward?: Cell;
  measureFrom?: Cell;
};

/**
 * Build the {@link AreaTemplate} for a resolved area attack from its shape (`decisions/0051`):
 *   - **Blast** — a `disk` at the aimed origin cell; LoS/cover measured **from that origin** (D4).
 *   - **Burst** — a `disk` centered on the user/target cell; LoS/cover measured **per-target**
 *     (D4 — no `measureFrom`); the attacker is excluded unconditionally (D3, applied by the caller).
 *   - **Line / Cone** — a directional template radiating from the **attacker's own space** (the
 *     apex, `origin: "self"`) toward the recorded `toward` cell; per-target LoS/cover (no D4 anchor).
 * `toward` for Line/Cone is `intent.toward`, falling back to the aimed cell so a `--cell`-aimed
 * directional shot still points somewhere. `undefined` when the template can't be anchored.
 */
export function areaTemplate(state: EncounterState, intent: RequestAttack, area: { kind: AreaKind; size: number }): AreaTemplate | undefined {
  const { kind, size } = area;
  if (kind === "blast" || kind === "burst") {
    const center = blastOrigin(state, intent.target);
    if (center === undefined) return undefined;
    return {
      selector: { shape: "disk", size, origin: "site", select: "character" },
      site: center,
      // D4: Blast measures LoS/cover from the origin cell; Burst measures per-target (no anchor).
      ...(kind === "blast" ? { measureFrom: center } : {}),
    };
  }
  // line | cone: apex = the attacker's own space; direction = the recorded toward (or the aimed cell).
  const attacker = state.combatants[intent.attacker];
  if (!attacker?.pos) return undefined; // an unplaced attacker has no apex to radiate from.
  const toward = (intent.toward as Cell | undefined) ?? blastOrigin(state, intent.target);
  if (toward === undefined) return undefined;
  return { selector: { shape: kind, size, origin: "self", select: "character" }, toward };
}
